/**
 * Chess Engine
 * 
 * Core game logic for chess, including move validation, check detection,
 * and game state management.
 * Implements requirements 3.2, 3.3, 7.1, 7.3 from the chess-game specification.
 */

import { Board } from './Board';
import { ChessPieceLogic } from './PieceLogic';
import {
  GameState,
  Move,
  Position,
  ChessPiece,
  PlayerColor,
  GameStatus,
  MoveResult,
  positionsEqual,
  isValidPosition
} from './types';

/**
 * ChessEngine manages the complete game state and enforces chess rules
 */
export class ChessEngine {
  private board: Board;
  private pieceLogic: ChessPieceLogic;
  private gameState: GameState;

  constructor() {
    this.board = new Board();
    this.pieceLogic = new ChessPieceLogic();
    this.gameState = this.createInitialGameState();
  }

  /**
   * Creates the initial game state
   */
  private createInitialGameState(): GameState {
    return {
      board: this.board.getBoard(),
      currentPlayer: 'white',
      moveHistory: [],
      capturedPieces: { white: [], black: [] },
      castlingRights: {
        whiteKingside: true,
        whiteQueenside: true,
        blackKingside: true,
        blackQueenside: true
      },
      enPassantSquare: undefined,
      halfMoveClock: 0,
      fullMoveNumber: 1,
      gameStatus: { type: 'active' },
      positionHistory: []
    };
  }

  /**
   * Initializes a new game with standard starting position
   * Requirement 1.2: Standard starting positions
   */
  public initializeGame(): void {
    this.board.initializeStartingPosition();
    this.gameState = this.createInitialGameState();
    this.gameState.board = this.board.getBoard();
  }

  /**
   * Gets all legal moves for a piece at a given position
   * Requirement 3.2: Prevent moves that leave king in check
   * 
   * @param position Position of the piece
   * @returns Array of legal destination positions
   */
  public getLegalMoves(position: Position): Position[] {
    const piece = this.board.getPieceAt(position);
    
    if (!piece || piece.color !== this.gameState.currentPlayer) {
      return [];
    }

    // Get all pseudo-legal moves (moves that follow piece movement rules)
    const pseudoLegalMoves = this.pieceLogic.getPieceMoves(
      position,
      this.board.getBoard(),
      this.gameState.moveHistory[this.gameState.moveHistory.length - 1]
    );

    // Filter out moves that would leave the king in check
    const legalMoves: Position[] = [];
    
    for (const move of pseudoLegalMoves) {
      if (this.isMoveLegal(position, move)) {
        legalMoves.push(move);
      }
    }

    // Add castling moves for kings
    if (piece.type === 'king') {
      const castlingMoves = this.getCastlingMoves(piece.color);
      legalMoves.push(...castlingMoves);
    }

    return legalMoves;
  }

  /**
   * Checks if en passant capture is possible
   * Requirements 5.1, 5.2, 5.3, 5.4: En passant detection
   * 
   * @param from Pawn position
   * @param to Target capture position
   * @returns True if en passant is legal
   */
  public canEnPassant(from: Position, to: Position): boolean {
    const piece = this.board.getPieceAt(from);
    
    // Must be a pawn
    if (!piece || piece.type !== 'pawn') {
      return false;
    }

    // Must have an en passant square available
    if (!this.gameState.enPassantSquare) {
      return false;
    }

    // Target must be the en passant square
    return positionsEqual(to, this.gameState.enPassantSquare);
  }

  /**
   * Performs an en passant capture
   * Requirements 5.2, 5.3: En passant execution
   * 
   * @param from Pawn position
   * @param to Target capture position (behind opponent's pawn)
   * @returns True if en passant was performed successfully
   */
  private performEnPassant(from: Position, to: Position): ChessPiece | null {
    if (!this.canEnPassant(from, to)) {
      return null;
    }

    const piece = this.board.getPieceAt(from);
    if (!piece) {
      return null;
    }

    // Calculate the position of the opponent's pawn to capture
    const captureDirection = piece.color === 'white' ? -1 : 1;
    const capturedPawnPos: Position = {
      file: to.file,
      rank: to.rank + captureDirection
    };

    // Get the captured pawn
    const capturedPawn = this.board.getPieceAt(capturedPawnPos);
    
    if (!capturedPawn) {
      return null;
    }

    // Move the capturing pawn diagonally
    piece.position = to;
    piece.hasMoved = true;
    piece.moveCount++;
    this.board.setPieceAt(to, piece);
    this.board.setPieceAt(from, null);

    // Remove the captured pawn
    this.board.setPieceAt(capturedPawnPos, null);

    return capturedPawn;
  }

  /**
   * Checks if a move is legal (doesn't leave own king in check)
   * 
   * @param from Starting position
   * @param to Ending position
   * @returns True if move is legal
   */
  private isMoveLegal(from: Position, to: Position): boolean {
    // Make a temporary move
    const originalBoard = this.board.getBoard();
    const piece = this.board.getPieceAt(from);
    const capturedPiece = this.board.getPieceAt(to);
    
    if (!piece) return false;

    // Simulate the move
    this.board.setPieceAt(to, piece);
    this.board.setPieceAt(from, null);

    // Check if own king is in check after the move
    const inCheck = this.isInCheck(piece.color);

    // Restore the board
    this.board.setBoard(originalBoard);

    return !inCheck;
  }

  /**
   * Gets castling moves for a player if castling is legal
   * Requirements 4.1-4.8: Castling conditions
   * 
   * @param player Player color
   * @returns Array of castling destination positions for the king
   */
  private getCastlingMoves(player: PlayerColor): Position[] {
    const moves: Position[] = [];
    const kingRank = player === 'white' ? 0 : 7;
    
    // Check kingside castling
    if (this.canCastle(player, 'kingside')) {
      moves.push({ file: 6, rank: kingRank }); // King moves to g-file
    }
    
    // Check queenside castling
    if (this.canCastle(player, 'queenside')) {
      moves.push({ file: 2, rank: kingRank }); // King moves to c-file
    }
    
    return moves;
  }

  /**
   * Checks if castling is legal for a player
   * Requirements 4.1-4.8: All castling conditions
   * 
   * @param player Player color
   * @param side Kingside or queenside
   * @returns True if castling is legal
   */
  public canCastle(player: PlayerColor, side: 'kingside' | 'queenside'): boolean {
    // Requirement 4.3: Cannot castle if king has moved
    const castlingRight = side === 'kingside' 
      ? (player === 'white' ? this.gameState.castlingRights.whiteKingside : this.gameState.castlingRights.blackKingside)
      : (player === 'white' ? this.gameState.castlingRights.whiteQueenside : this.gameState.castlingRights.blackQueenside);
    
    if (!castlingRight) {
      return false;
    }

    // Requirement 4.5: Cannot castle if king is in check
    if (this.isInCheck(player)) {
      return false;
    }

    const kingRank = player === 'white' ? 0 : 7;
    const kingFile = 4; // King starts on e-file
    const kingPos: Position = { file: kingFile, rank: kingRank };
    
    // Verify king is actually at starting position
    const king = this.board.getPieceAt(kingPos);
    if (!king || king.type !== 'king' || king.color !== player) {
      return false;
    }

    const opponent: PlayerColor = player === 'white' ? 'black' : 'white';
    const board = this.board.getBoard();

    if (side === 'kingside') {
      // Kingside castling: King moves from e1/e8 to g1/g8, Rook from h1/h8 to f1/f8
      const rookFile = 7; // h-file
      const rookPos: Position = { file: rookFile, rank: kingRank };
      
      // Requirement 4.4: Rook must not have moved
      const rook = this.board.getPieceAt(rookPos);
      if (!rook || rook.type !== 'rook' || rook.color !== player) {
        return false;
      }

      // Requirement 4.8: No pieces between king and rook
      const f_file = 5; // f-file
      const g_file = 6; // g-file
      
      if (board[kingRank][f_file] !== null || board[kingRank][g_file] !== null) {
        return false;
      }

      // Requirement 4.6: King cannot pass through attacked square
      const f_square: Position = { file: f_file, rank: kingRank };
      if (this.pieceLogic.isSquareAttacked(f_square, opponent, board)) {
        return false;
      }

      // Requirement 4.7: King cannot end up in check
      const g_square: Position = { file: g_file, rank: kingRank };
      if (this.pieceLogic.isSquareAttacked(g_square, opponent, board)) {
        return false;
      }

      return true;
    } else {
      // Queenside castling: King moves from e1/e8 to c1/c8, Rook from a1/a8 to d1/d8
      const rookFile = 0; // a-file
      const rookPos: Position = { file: rookFile, rank: kingRank };
      
      // Requirement 4.4: Rook must not have moved
      const rook = this.board.getPieceAt(rookPos);
      if (!rook || rook.type !== 'rook' || rook.color !== player) {
        return false;
      }

      // Requirement 4.8: No pieces between king and rook
      const b_file = 1; // b-file
      const c_file = 2; // c-file
      const d_file = 3; // d-file
      
      if (board[kingRank][b_file] !== null || 
          board[kingRank][c_file] !== null || 
          board[kingRank][d_file] !== null) {
        return false;
      }

      // Requirement 4.6: King cannot pass through attacked square
      const d_square: Position = { file: d_file, rank: kingRank };
      if (this.pieceLogic.isSquareAttacked(d_square, opponent, board)) {
        return false;
      }

      // Requirement 4.7: King cannot end up in check
      const c_square: Position = { file: c_file, rank: kingRank };
      if (this.pieceLogic.isSquareAttacked(c_square, opponent, board)) {
        return false;
      }

      return true;
    }
  }

  /**
   * Performs a castling move
   * Requirements 4.1, 4.2: Execute kingside and queenside castling
   * 
   * @param player Player color
   * @param side Kingside or queenside
   * @returns True if castling was performed successfully
   */
  private performCastling(player: PlayerColor, side: 'kingside' | 'queenside'): boolean {
    if (!this.canCastle(player, side)) {
      return false;
    }

    const kingRank = player === 'white' ? 0 : 7;
    const kingFile = 4; // e-file
    const kingPos: Position = { file: kingFile, rank: kingRank };
    const king = this.board.getPieceAt(kingPos);
    
    if (!king) {
      return false;
    }

    if (side === 'kingside') {
      // Kingside: King e1->g1, Rook h1->f1 (or e8->g8, h8->f8 for black)
      const rookPos: Position = { file: 7, rank: kingRank };
      const rook = this.board.getPieceAt(rookPos);
      
      if (!rook) {
        return false;
      }

      // Move king to g-file
      const newKingPos: Position = { file: 6, rank: kingRank };
      king.position = newKingPos;
      king.hasMoved = true;
      king.moveCount++;
      this.board.setPieceAt(newKingPos, king);
      this.board.setPieceAt(kingPos, null);

      // Move rook to f-file
      const newRookPos: Position = { file: 5, rank: kingRank };
      rook.position = newRookPos;
      rook.hasMoved = true;
      rook.moveCount++;
      this.board.setPieceAt(newRookPos, rook);
      this.board.setPieceAt(rookPos, null);
    } else {
      // Queenside: King e1->c1, Rook a1->d1 (or e8->c8, a8->d8 for black)
      const rookPos: Position = { file: 0, rank: kingRank };
      const rook = this.board.getPieceAt(rookPos);
      
      if (!rook) {
        return false;
      }

      // Move king to c-file
      const newKingPos: Position = { file: 2, rank: kingRank };
      king.position = newKingPos;
      king.hasMoved = true;
      king.moveCount++;
      this.board.setPieceAt(newKingPos, king);
      this.board.setPieceAt(kingPos, null);

      // Move rook to d-file
      const newRookPos: Position = { file: 3, rank: kingRank };
      rook.position = newRookPos;
      rook.hasMoved = true;
      rook.moveCount++;
      this.board.setPieceAt(newRookPos, rook);
      this.board.setPieceAt(rookPos, null);
    }

    // Update castling rights - player can no longer castle
    if (player === 'white') {
      this.gameState.castlingRights.whiteKingside = false;
      this.gameState.castlingRights.whiteQueenside = false;
    } else {
      this.gameState.castlingRights.blackKingside = false;
      this.gameState.castlingRights.blackQueenside = false;
    }

    return true;
  }

  /**
   * Makes a move on the board
   * Requirement 3.3: Validate all moves
   * 
   * @param from Starting position
   * @param to Ending position
   * @param promotion Optional promotion piece type
   * @returns MoveResult indicating success or failure
   */
  public makeMove(from: Position, to: Position, promotion?: 'queen' | 'rook' | 'bishop' | 'knight'): MoveResult {
    // Validate positions
    if (!isValidPosition(from) || !isValidPosition(to)) {
      return {
        success: false,
        error: 'Invalid position',
        gameStatus: this.gameState.gameStatus
      };
    }

    // Get the piece
    const piece = this.board.getPieceAt(from);
    
    if (!piece) {
      return {
        success: false,
        error: 'No piece at starting position',
        gameStatus: this.gameState.gameStatus
      };
    }

    // Check if it's the correct player's turn
    if (piece.color !== this.gameState.currentPlayer) {
      return {
        success: false,
        error: 'Not your turn',
        gameStatus: this.gameState.gameStatus
      };
    }

    // Check if this is a castling move
    const isCastlingMove = piece.type === 'king' && Math.abs(to.file - from.file) === 2;
    
    if (isCastlingMove) {
      // Determine castling side based on king's destination
      const side = to.file === 6 ? 'kingside' : 'queenside';
      
      if (!this.canCastle(piece.color, side)) {
        return {
          success: false,
          error: 'Castling not allowed',
          gameStatus: this.gameState.gameStatus
        };
      }

      // Perform castling
      if (!this.performCastling(piece.color, side)) {
        return {
          success: false,
          error: 'Failed to perform castling',
          gameStatus: this.gameState.gameStatus
        };
      }

      // Update game state
      this.gameState.board = this.board.getBoard();
      this.gameState.halfMoveClock++;
      // Clear en passant square after any move
      this.gameState.enPassantSquare = undefined;

      // Create move record for castling
      const move: Move = {
        from,
        to,
        piece: { ...piece },
        isCastling: true,
        isCheck: false,
        isCheckmate: false,
        isEnPassant: false,
        notation: side === 'kingside' ? 'O-O' : 'O-O-O',
        timestamp: Date.now()
      };

      // Determine opponent and switch players BEFORE checking game-ending conditions
      const opponent: PlayerColor = this.gameState.currentPlayer === 'white' ? 'black' : 'white';
      const movingPlayer = this.gameState.currentPlayer;
      
      // Switch players before checking game-ending conditions
      if (this.gameState.currentPlayer === 'black') {
        this.gameState.fullMoveNumber++;
      }
      this.gameState.currentPlayer = opponent;
      
      // Check if opponent is in check
      const opponentInCheck = this.isInCheck(opponent);
      
      if (opponentInCheck) {
        move.isCheck = true;
        move.notation += '+';
        
        // Check if it's checkmate
        if (this.isCheckmate(opponent)) {
          move.isCheckmate = true;
          move.notation = move.notation.replace('+', '#');
          this.gameState.gameStatus = {
            type: 'checkmate',
            winner: movingPlayer
          };
        } else {
          this.gameState.gameStatus = {
            type: 'check',
            player: opponent
          };
        }
      } else {
        // Check for stalemate
        if (this.isStalemate(opponent)) {
          this.gameState.gameStatus = { type: 'stalemate' };
        } else {
          this.gameState.gameStatus = { type: 'active' };
        }
      }

      // Add move to history
      this.gameState.moveHistory.push(move);

      // Update position history for threefold repetition detection
      this.updatePositionHistory();

      // Check for draw conditions (only if game is not already over)
      if (this.gameState.gameStatus.type === 'active') {
        this.checkDrawConditions();
      }

      return {
        success: true,
        move,
        gameStatus: this.gameState.gameStatus
      };
    }

    // Check if the move is legal (non-castling move)
    const legalMoves = this.getLegalMoves(from);
    const isLegal = legalMoves.some(move => positionsEqual(move, to));
    
    if (!isLegal) {
      return {
        success: false,
        error: 'Illegal move',
        gameStatus: this.gameState.gameStatus
      };
    }

    // Check if this is an en passant move
    const isEnPassantMove = piece.type === 'pawn' && this.canEnPassant(from, to);
    let capturedPiece: ChessPiece | null = null;

    if (isEnPassantMove) {
      // Perform en passant capture
      capturedPiece = this.performEnPassant(from, to);
      
      if (!capturedPiece) {
        return {
          success: false,
          error: 'Failed to perform en passant',
          gameStatus: this.gameState.gameStatus
        };
      }
    } else {
      // Execute normal move
      capturedPiece = this.board.getPieceAt(to);
    }
    
    // Handle pawn promotion
    // Requirement 6.1: Detect when pawn reaches opposite end
    if (piece.type === 'pawn') {
      const promotionRank = piece.color === 'white' ? 7 : 0;
      if (to.rank === promotionRank) {
        // Requirement 6.4: Don't allow game to continue until piece is selected
        if (!promotion) {
          return {
            success: false,
            error: 'Pawn promotion requires piece selection',
            gameStatus: this.gameState.gameStatus
          };
        }
        // Requirement 6.3: Replace pawn with chosen piece
        piece.type = promotion;
      }
    }

    // Update castling rights when king or rook moves
    if (piece.type === 'king') {
      if (piece.color === 'white') {
        this.gameState.castlingRights.whiteKingside = false;
        this.gameState.castlingRights.whiteQueenside = false;
      } else {
        this.gameState.castlingRights.blackKingside = false;
        this.gameState.castlingRights.blackQueenside = false;
      }
    } else if (piece.type === 'rook') {
      // Update castling rights when rook moves from starting position
      if (piece.color === 'white') {
        if (from.file === 0 && from.rank === 0) {
          this.gameState.castlingRights.whiteQueenside = false;
        } else if (from.file === 7 && from.rank === 0) {
          this.gameState.castlingRights.whiteKingside = false;
        }
      } else {
        if (from.file === 0 && from.rank === 7) {
          this.gameState.castlingRights.blackQueenside = false;
        } else if (from.file === 7 && from.rank === 7) {
          this.gameState.castlingRights.blackKingside = false;
        }
      }
    }

    // Update castling rights if a rook is captured
    if (capturedPiece && capturedPiece.type === 'rook') {
      if (capturedPiece.color === 'white') {
        if (to.file === 0 && to.rank === 0) {
          this.gameState.castlingRights.whiteQueenside = false;
        } else if (to.file === 7 && to.rank === 0) {
          this.gameState.castlingRights.whiteKingside = false;
        }
      } else {
        if (to.file === 0 && to.rank === 7) {
          this.gameState.castlingRights.blackQueenside = false;
        } else if (to.file === 7 && to.rank === 7) {
          this.gameState.castlingRights.blackKingside = false;
        }
      }
    }

    // Make the move on the board (skip if en passant, already done)
    if (!isEnPassantMove) {
      // Update piece position and move tracking
      piece.position = to;
      piece.hasMoved = true;
      piece.moveCount++;

      this.board.setPieceAt(to, piece);
      this.board.setPieceAt(from, null);
    }

    // Update game state
    this.gameState.board = this.board.getBoard();

    // Track en passant square for next move
    // Requirement 5.1: Enable en passant when pawn moves two squares
    if (piece.type === 'pawn' && Math.abs(to.rank - from.rank) === 2) {
      // Set en passant square to the square the pawn passed over
      const direction = piece.color === 'white' ? 1 : -1;
      this.gameState.enPassantSquare = {
        file: from.file,
        rank: from.rank + direction
      };
    } else {
      // Requirement 5.4: Disable en passant if any other move is made
      this.gameState.enPassantSquare = undefined;
    }

    // Track captured piece
    if (capturedPiece) {
      this.gameState.capturedPieces[this.gameState.currentPlayer].push(capturedPiece);
      this.gameState.halfMoveClock = 0; // Reset on capture
    } else if (piece.type === 'pawn') {
      this.gameState.halfMoveClock = 0; // Reset on pawn move
    } else {
      this.gameState.halfMoveClock++;
    }

    // Create move record
    const move: Move = {
      from,
      to,
      piece: { ...piece },
      captured: capturedPiece || undefined,
      promotion,
      isCheck: false,
      isCheckmate: false,
      isCastling: false,
      isEnPassant: isEnPassantMove,
      notation: this.getMoveNotation(from, to, piece, capturedPiece, isEnPassantMove, promotion),
      timestamp: Date.now()
    };

    // Determine opponent and switch players BEFORE checking game-ending conditions
    const opponent: PlayerColor = this.gameState.currentPlayer === 'white' ? 'black' : 'white';
    const movingPlayer = this.gameState.currentPlayer;
    
    // Switch players before checking game-ending conditions
    // This is necessary because getLegalMoves checks currentPlayer
    if (this.gameState.currentPlayer === 'black') {
      this.gameState.fullMoveNumber++;
    }
    this.gameState.currentPlayer = opponent;
    
    // Check if opponent is in check
    const opponentInCheck = this.isInCheck(opponent);
    
    if (opponentInCheck) {
      move.isCheck = true;
      
      // Check if it's checkmate
      if (this.isCheckmate(opponent)) {
        move.isCheckmate = true;
        this.gameState.gameStatus = {
          type: 'checkmate',
          winner: movingPlayer
        };
      } else {
        this.gameState.gameStatus = {
          type: 'check',
          player: opponent
        };
      }
    } else {
      // Check for stalemate
      if (this.isStalemate(opponent)) {
        this.gameState.gameStatus = { type: 'stalemate' };
      } else {
        this.gameState.gameStatus = { type: 'active' };
      }
    }

    // Add move to history
    this.gameState.moveHistory.push(move);

    // Update position history for threefold repetition detection
    this.updatePositionHistory();

    // Check for draw conditions (only if game is not already over)
    if (this.gameState.gameStatus.type === 'active') {
      this.checkDrawConditions();
    }

    return {
      success: true,
      move,
      gameStatus: this.gameState.gameStatus
    };
  }

  /**
   * Checks if a player's king is in check
   * Requirement 7.1: Check detection
   * 
   * @param player Player color to check
   * @returns True if the player's king is in check
   */
  public isInCheck(player: PlayerColor): boolean {
    // Find the king
    const kingPosition = this.board.findKing(player);
    
    if (!kingPosition) {
      return false; // No king found (shouldn't happen in valid game)
    }

    // Check if any opponent piece can attack the king
    const opponent: PlayerColor = player === 'white' ? 'black' : 'white';
    return this.pieceLogic.isSquareAttacked(kingPosition, opponent, this.board.getBoard());
  }

  /**
   * Checks if a player is in checkmate
   * Requirement 8.1: Checkmate detection
   * 
   * @param player Player color to check
   * @returns True if the player is in checkmate
   */
  public isCheckmate(player: PlayerColor): boolean {
    // Must be in check
    if (!this.isInCheck(player)) {
      return false;
    }

    // Check if player has any legal moves
    return !this.hasLegalMoves(player);
  }

  /**
   * Checks if a player is in stalemate
   * Requirement 9.1: Stalemate detection
   * 
   * @param player Player color to check
   * @returns True if the player is in stalemate
   */
  public isStalemate(player: PlayerColor): boolean {
    // Must NOT be in check
    if (this.isInCheck(player)) {
      return false;
    }

    // Check if player has any legal moves
    return !this.hasLegalMoves(player);
  }

  /**
   * Checks if a player has any legal moves
   * 
   * @param player Player color to check
   * @returns True if the player has at least one legal move
   */
  private hasLegalMoves(player: PlayerColor): boolean {
    const pieces = this.board.getPiecesByColor(player);
    
    for (const piece of pieces) {
      const legalMoves = this.getLegalMoves(piece.position);
      if (legalMoves.length > 0) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Gets the current game state
   * 
   * @returns Current game state
   */
  public getGameState(): GameState {
    return {
      ...this.gameState,
      board: this.board.getBoard()
    };
  }

  /**
   * Sets the game state (used for move simulation in AI)
   * 
   * @param state Game state to set
   */
  public setGameState(state: GameState): void {
    // Deep copy arrays to prevent reference sharing between engines
    this.gameState = {
      ...state,
      moveHistory: [...state.moveHistory],
      capturedPieces: {
        white: [...state.capturedPieces.white],
        black: [...state.capturedPieces.black]
      },
      positionHistory: [...state.positionHistory]
    };
    // If the board in state is a Board object, use it directly
    // Otherwise, reconstruct the board from the state
    if (state.board instanceof Board) {
      this.board = state.board;
    } else {
      // Board might be serialized, reconstruct it
      this.board = new Board();
      const boardArray = (state.board as any).board || state.board;
      if (Array.isArray(boardArray)) {
        for (let rank = 0; rank < 8; rank++) {
          for (let file = 0; file < 8; file++) {
            this.board.setPieceAt({ rank, file }, boardArray[rank][file]);
          }
        }
      }
    }
  }

  /**
   * Gets the current player
   * 
   * @returns Current player color
   */
  public getCurrentPlayer(): PlayerColor {
    return this.gameState.currentPlayer;
  }

  /**
   * Gets the game status
   * 
   * @returns Current game status
   */
  public getGameStatus(): GameStatus {
    return this.gameState.gameStatus;
  }

  /**
   * Gets the move history
   * 
   * @returns Array of all moves made
   */
  public getMoveHistory(): Move[] {
    return [...this.gameState.moveHistory];
  }

  /**
   * Gets captured pieces
   * 
   * @returns Captured pieces for both players
   */
  public getCapturedPieces(): { white: ChessPiece[], black: ChessPiece[] } {
    return {
      white: [...this.gameState.capturedPieces.white],
      black: [...this.gameState.capturedPieces.black]
    };
  }

  /**
   * Generates algebraic notation for a move
   * 
   * @param from Starting position
   * @param to Ending position
   * @param piece Piece being moved
   * @param captured Captured piece (if any)
   * @param isEnPassant Whether this is an en passant capture
   * @param promotion Promotion piece type (if any)
   * @returns Algebraic notation string
   */
  private getMoveNotation(
    from: Position, 
    to: Position, 
    piece: ChessPiece, 
    captured?: ChessPiece | null, 
    isEnPassant: boolean = false,
    promotion?: 'queen' | 'rook' | 'bishop' | 'knight'
  ): string {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const toSquare = `${files[to.file]}${to.rank + 1}`;
    
    // Pawn moves (including promoted pawns)
    // Note: piece.type may have been changed to the promoted piece already
    const isPawnMove = promotion !== undefined || piece.type === 'pawn';
    
    if (isPawnMove) {
      let notation = '';
      if (captured || isEnPassant) {
        notation = `${files[from.file]}x${toSquare}`;
      } else {
        notation = toSquare;
      }
      
      // Add promotion notation
      if (promotion) {
        const promotionSymbols: Record<string, string> = {
          'queen': 'Q',
          'rook': 'R',
          'bishop': 'B',
          'knight': 'N'
        };
        notation += `=${promotionSymbols[promotion]}`;
      }
      
      // Add e.p. suffix for en passant
      if (isEnPassant) {
        notation += ' e.p.';
      }
      
      return notation;
    }
    
    // Piece moves - use standard chess notation symbols
    const pieceSymbols: Record<string, string> = {
      'king': 'K',
      'queen': 'Q',
      'rook': 'R',
      'bishop': 'B',
      'knight': 'N'
    };
    
    const pieceSymbol = pieceSymbols[piece.type] || piece.type.charAt(0).toUpperCase();
    const captureSymbol = captured ? 'x' : '';
    
    return `${pieceSymbol}${captureSymbol}${toSquare}`;
  }

  /**
   * Gets the board instance
   * 
   * @returns Board instance
   */
  public getBoard(): Board {
    return this.board;
  }

  /**
   * Checks if the game is over
   * 
   * @returns True if game is over
   */
  public isGameOver(): boolean {
    const status = this.gameState.gameStatus;
    return status.type === 'checkmate' || 
           status.type === 'stalemate' || 
           status.type === 'draw' ||
           status.type === 'resignation' ||
           status.type === 'timeout';
  }

  /**
   * Checks if the fifty-move rule applies
   * Requirement 10.1: Draw by fifty-move rule
   * 
   * The fifty-move rule states that a player can claim a draw if no capture
   * has been made and no pawn has been moved in the last fifty moves (100 half-moves).
   * 
   * @returns True if fifty moves have occurred without pawn movement or capture
   */
  public checkFiftyMoveRule(): boolean {
    return this.gameState.halfMoveClock >= 100;
  }

  /**
   * Checks if threefold repetition has occurred
   * Requirement 10.2: Draw by threefold repetition
   * 
   * Threefold repetition occurs when the same position appears three times
   * with the same player to move, same castling rights, and same en passant availability.
   * 
   * @returns True if the current position has occurred three times
   */
  public checkThreefoldRepetition(): boolean {
    // Generate FEN-like position string for current position
    const currentPosition = this.generatePositionHash();
    
    // Count occurrences of this position in history
    let occurrences = 1; // Current position counts as 1
    
    for (const historicalPosition of this.gameState.positionHistory) {
      if (historicalPosition === currentPosition) {
        occurrences++;
        if (occurrences >= 3) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Generates a position hash for threefold repetition detection
   * Includes board state, current player, castling rights, and en passant square
   * 
   * @returns String representation of the position
   */
  private generatePositionHash(): string {
    const board = this.board.getBoard();
    let hash = '';
    
    // Encode board position
    for (let rank = 7; rank >= 0; rank--) {
      let emptyCount = 0;
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece === null) {
          emptyCount++;
        } else {
          if (emptyCount > 0) {
            hash += emptyCount.toString();
            emptyCount = 0;
          }
          // Use uppercase for white, lowercase for black
          const pieceChar = piece.type.charAt(0);
          hash += piece.color === 'white' ? pieceChar.toUpperCase() : pieceChar.toLowerCase();
        }
      }
      if (emptyCount > 0) {
        hash += emptyCount.toString();
      }
      if (rank > 0) {
        hash += '/';
      }
    }
    
    // Add current player
    hash += ` ${this.gameState.currentPlayer.charAt(0)}`;
    
    // Add castling rights
    let castling = '';
    if (this.gameState.castlingRights.whiteKingside) castling += 'K';
    if (this.gameState.castlingRights.whiteQueenside) castling += 'Q';
    if (this.gameState.castlingRights.blackKingside) castling += 'k';
    if (this.gameState.castlingRights.blackQueenside) castling += 'q';
    hash += ` ${castling || '-'}`;
    
    // Add en passant square
    if (this.gameState.enPassantSquare) {
      const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
      hash += ` ${files[this.gameState.enPassantSquare.file]}${this.gameState.enPassantSquare.rank + 1}`;
    } else {
      hash += ' -';
    }
    
    return hash;
  }

  /**
   * Checks if there is insufficient material for checkmate
   * Requirement 10.3-10.7: Draw by insufficient material
   * 
   * Insufficient material occurs when neither player has enough pieces to checkmate:
   * - King vs King
   * - King + Bishop vs King
   * - King + Knight vs King
   * - King + Bishop vs King + Bishop (same color bishops)
   * 
   * @returns True if neither player can achieve checkmate
   */
  public checkInsufficientMaterial(): boolean {
    const whitePieces = this.board.getPiecesByColor('white');
    const blackPieces = this.board.getPiecesByColor('black');
    
    // Count pieces by type for each color
    const whiteCounts = this.countPieceTypes(whitePieces);
    const blackCounts = this.countPieceTypes(blackPieces);
    
    const whiteTotalPieces = whitePieces.length;
    const blackTotalPieces = blackPieces.length;
    
    // Requirement 10.4: King vs King
    if (whiteTotalPieces === 1 && blackTotalPieces === 1) {
      return true;
    }
    
    // Requirement 10.5: King + Bishop vs King
    if (whiteTotalPieces === 2 && blackTotalPieces === 1 && whiteCounts.bishop === 1) {
      return true;
    }
    if (blackTotalPieces === 2 && whiteTotalPieces === 1 && blackCounts.bishop === 1) {
      return true;
    }
    
    // Requirement 10.6: King + Knight vs King
    if (whiteTotalPieces === 2 && blackTotalPieces === 1 && whiteCounts.knight === 1) {
      return true;
    }
    if (blackTotalPieces === 2 && whiteTotalPieces === 1 && blackCounts.knight === 1) {
      return true;
    }
    
    // Requirement 10.7: King + Bishop vs King + Bishop (same color bishops)
    if (whiteTotalPieces === 2 && blackTotalPieces === 2 && 
        whiteCounts.bishop === 1 && blackCounts.bishop === 1) {
      // Check if bishops are on same color squares
      const whiteBishop = whitePieces.find(p => p.type === 'bishop');
      const blackBishop = blackPieces.find(p => p.type === 'bishop');
      
      if (whiteBishop && blackBishop) {
        // Bishops are on same color if (file + rank) has same parity
        const whiteBishopColor = (whiteBishop.position.file + whiteBishop.position.rank) % 2;
        const blackBishopColor = (blackBishop.position.file + blackBishop.position.rank) % 2;
        
        if (whiteBishopColor === blackBishopColor) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Counts pieces by type for a given set of pieces
   * 
   * @param pieces Array of chess pieces
   * @returns Object with counts for each piece type
   */
  private countPieceTypes(pieces: ChessPiece[]): Record<string, number> {
    const counts: Record<string, number> = {
      king: 0,
      queen: 0,
      rook: 0,
      bishop: 0,
      knight: 0,
      pawn: 0
    };
    
    for (const piece of pieces) {
      counts[piece.type]++;
    }
    
    return counts;
  }

  /**
   * Updates position history after a move
   * Should be called after each move to track positions for threefold repetition
   */
  private updatePositionHistory(): void {
    const currentPosition = this.generatePositionHash();
    this.gameState.positionHistory.push(currentPosition);
  }

  /**
   * Checks all draw conditions and updates game status if applicable
   * Should be called after each move
   * 
   * @returns True if a draw condition is detected
   */
  public checkDrawConditions(): boolean {
    // Check fifty-move rule
    if (this.checkFiftyMoveRule()) {
      this.gameState.gameStatus = {
        type: 'draw',
        reason: 'fifty-move'
      };
      return true;
    }
    
    // Check threefold repetition
    if (this.checkThreefoldRepetition()) {
      this.gameState.gameStatus = {
        type: 'draw',
        reason: 'repetition'
      };
      return true;
    }
    
    // Check insufficient material
    if (this.checkInsufficientMaterial()) {
      this.gameState.gameStatus = {
        type: 'draw',
        reason: 'insufficient-material'
      };
      return true;
    }
    
    return false;
  }

  /**
   * Offers a draw to the opponent
   * Requirement 11.1: Present draw offer to opponent
   * Requirement 11.6: Allow draw offers at any point during the game
   * 
   * This method initiates a draw offer from the current player.
   * The offer must be accepted by the opponent using acceptDraw().
   * 
   * @returns True if draw offer was successfully initiated
   */
  public offerDraw(): boolean {
    // Can only offer draw if game is active or in check
    if (this.gameState.gameStatus.type !== 'active' && 
        this.gameState.gameStatus.type !== 'check') {
      return false;
    }

    // Draw offer is successful - it's up to the UI/multiplayer service
    // to present this to the opponent and call acceptDraw() or declineDraw()
    return true;
  }

  /**
   * Accepts a draw offer and ends the game as a draw
   * Requirement 11.2: End game as draw when offer is accepted
   * 
   * This method should be called when the opponent accepts a draw offer.
   * It immediately ends the game with a draw result.
   */
  public acceptDraw(): void {
    // Update game status to draw by agreement
    this.gameState.gameStatus = {
      type: 'draw',
      reason: 'agreement'
    };
  }

  /**
   * Declines a draw offer and continues the game normally
   * Requirement 11.3: Continue game normally when offer is declined
   * 
   * This method should be called when the opponent declines a draw offer.
   * The game continues without any state changes.
   */
  public declineDraw(): void {
    // No state changes needed - game continues normally
    // This method exists for API completeness and clarity
  }

  /**
   * Resigns the game for the specified player
   * Requirement 11.4: End game immediately and declare opponent as winner
   * 
   * When a player resigns, the game ends immediately and the opponent
   * is declared the winner.
   * 
   * @param player The player who is resigning
   */
  public resign(player: PlayerColor): void {
    // Determine the winner (opponent of the resigning player)
    const winner: PlayerColor = player === 'white' ? 'black' : 'white';
    
    // Update game status to resignation
    this.gameState.gameStatus = {
      type: 'resignation',
      winner
    };
  }

  /**
   * Undoes the last move(s) in single-player mode
   * Requirements 23.1, 23.2, 23.3, 23.4: Undo move functionality
   * 
   * In single-player mode, this undoes both the player's last move and the AI's response.
   * Supports multiple consecutive undos up to the start of the game.
   * Restores board state, move history, captured pieces, castling rights, en passant,
   * and clock times.
   * 
   * @param movesToUndo Number of moves to undo (default 2 for player + AI move)
   * @returns True if undo was successful, false if no moves to undo
   */
  public undoMove(movesToUndo: number = 2): boolean {
    // Requirement 23.3: Support multiple consecutive undos up to the start of the game
    if (this.gameState.moveHistory.length === 0) {
      return false; // No moves to undo
    }

    // Limit to available moves
    const actualMovesToUndo = Math.min(movesToUndo, this.gameState.moveHistory.length);
    
    if (actualMovesToUndo === 0) {
      return false;
    }

    // Store the moves we're undoing for restoration
    const movesToRestore: Move[] = [];
    
    for (let i = 0; i < actualMovesToUndo; i++) {
      const lastMove = this.gameState.moveHistory.pop();
      if (!lastMove) {
        break;
      }
      movesToRestore.unshift(lastMove); // Add to front to maintain order
    }

    // Rebuild the game state from scratch by replaying all remaining moves
    // This ensures all state (castling rights, en passant, etc.) is correct
    const remainingMoves = [...this.gameState.moveHistory];
    
    // Reset to initial state
    this.board.initializeStartingPosition();
    this.gameState = this.createInitialGameState();
    this.gameState.board = this.board.getBoard();

    // Replay all remaining moves
    for (const move of remainingMoves) {
      // Reconstruct the move by replaying it
      const promotion = move.promotion as 'queen' | 'rook' | 'bishop' | 'knight' | undefined;
      const result = this.makeMove(move.from, move.to, promotion);
      
      if (!result.success) {
        // If replay fails, something went wrong - restore the moves we tried to undo
        for (const restoredMove of movesToRestore) {
          this.gameState.moveHistory.push(restoredMove);
        }
        return false;
      }
    }

    // Requirement 23.4: Update move history to reflect undone moves
    // (Already done by popping from moveHistory above)

    return true;
  }

  /**
   * Gets the number of moves that can be undone
   * 
   * @returns Number of moves in the move history
   */
  public getUndoableMovesCount(): number {
    return this.gameState.moveHistory.length;
  }

  /**
   * Checks if undo is available
   * 
   * @returns True if there are moves that can be undone
   */
  public canUndo(): boolean {
    return this.gameState.moveHistory.length > 0;
  }
}
